import React from 'react';
import { CheckCircle, TrendingUp, Award } from 'lucide-react';
import VerifiedBadge from './VerifiedBadge';

/**
 * VERIFICATION MILESTONE COMPONENT
 * 
 * Shows progress toward automatic verification through sales
 * Users get verified automatically after 500 sales
 */
export default function VerificationMilestone({ 
  salesCount = 0, 
  isVerified = false,
  verificationType = 'none',
  className = ''
}) {
  const VERIFICATION_THRESHOLD = 500;
  const percentComplete = Math.min((salesCount / VERIFICATION_THRESHOLD) * 100, 100);
  const salesRemaining = Math.max(VERIFICATION_THRESHOLD - salesCount, 0);
  
  // If verified through sales milestone (not paid), show completion status
  if (isVerified && verificationType === 'sales_milestone') {
    return (
      <div className={`bg-gradient-to-r from-gray-900/40 via-gray-800/40 to-gray-900/40 border-[1px] border-gray-500 rounded-xl p-6 shadow-2xl ${className}`}>
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gray-400/20 to-gray-600/20 border-[1px] border-gray-500 flex items-center justify-center shadow-lg">
              <Award className="w-6 h-6 text-gray-300" />
            </div>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-lg font-semibold text-white">Verified Creator</h3>
              <VerifiedBadge size="md" verificationType={verificationType} />
            </div>
            <p className="text-gray-300 text-sm mb-2">
              Automatically verified after reaching {VERIFICATION_THRESHOLD} sales!
            </p>
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <CheckCircle className="w-4 h-4 text-gray-400" />
              <span>Total Sales: <span className="font-semibold text-white">{salesCount.toLocaleString()}</span></span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show progress toward verification milestone
  return (
    <div className={`bg-black border-[1px] border-[#2a2a2a] rounded-xl p-6 shadow-2xl ${className}`}>
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gray-400/20 to-gray-600/20 flex items-center justify-center shadow-inner border-[1px] border-[#2a2a2a]">
            <TrendingUp className="w-6 h-6 text-gray-300" />
          </div>
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-lg font-semibold text-white">
              {verificationType === 'paid' ? 'Sales Milestone Progress' : 'Verification Milestone'}
            </h3>
            {verificationType === 'paid' && (
              <VerifiedBadge size="md" verificationType="sales_milestone" />
            )}
          </div>
          <p className="text-gray-400 text-sm mb-4">
            {verificationType === 'paid' 
              ? `You're premium verified! Track your progress to ${VERIFICATION_THRESHOLD} sales for the chrome badge.`
              : `Get automatically verified after reaching ${VERIFICATION_THRESHOLD} sales`
            }
          </p>
          
          {/* Progress Bar */}
          <div className="mb-3">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-300">
                <span className="font-semibold text-white">{salesCount}</span> / {VERIFICATION_THRESHOLD} sales
              </span>
              <span className="text-gray-300 font-medium">{percentComplete.toFixed(1)}%</span>
            </div>
            <div className="relative w-full h-3 bg-black border-[1px] border-[#2a2a2a] rounded-full overflow-visible shadow-inner">
              <div 
                className="h-full bg-gradient-to-r from-gray-400 via-gray-300 to-gray-500 transition-all duration-500 ease-out rounded-full shadow-lg"
                style={{ width: `${percentComplete}%` }}
              />
              
              {/* Milestone markers */}
              <div className="absolute top-0 left-[20%] w-[2px] h-full bg-gray-600 z-10" title="100 sales">
                <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-4 h-4 rounded-full border-[2px] border-gray-600 bg-black flex items-center justify-center">
                  {salesCount >= 100 && <div className="w-2 h-2 rounded-full bg-gray-400"></div>}
                </div>
              </div>
              <div className="absolute top-0 left-[50%] w-[2px] h-full bg-gray-600 z-10" title="250 sales">
                <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-4 h-4 rounded-full border-[2px] border-gray-600 bg-black flex items-center justify-center">
                  {salesCount >= 250 && <div className="w-2 h-2 rounded-full bg-gray-400"></div>}
                </div>
              </div>
            </div>
            
            {/* Milestone labels */}
            <div className="relative w-full text-[10px] text-gray-500 mt-1 h-3">
              <span className="absolute left-0">0</span>
              <span className={`absolute left-[20%] -translate-x-1/2 ${salesCount >= 100 ? 'text-gray-400 font-semibold' : ''}`}>100</span>
              <span className={`absolute left-[50%] -translate-x-1/2 ${salesCount >= 250 ? 'text-gray-400 font-semibold' : ''}`}>250</span>
              <span className={`absolute right-0 ${salesCount >= 500 ? 'text-gray-400 font-semibold' : ''}`}>500</span>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-3 mt-4">
            <div className="bg-black border-[1px] border-[#2a2a2a] rounded-xl p-3">
              <p className="text-xs text-gray-400 mb-1">Sales Remaining</p>
              <p className="text-xl font-bold text-white">{salesRemaining.toLocaleString()}</p>
            </div>
            <div className="bg-black border-[1px] border-[#2a2a2a] rounded-xl p-3">
              <p className="text-xs text-gray-400 mb-1">Current Sales</p>
              <p className="text-xl font-bold text-white">{salesCount.toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

